/**
 * @file: Financing.jsx
 * @status: Sovereign Fintech Gateway (V12.6.1) - COMPACT & ACCESSIBILITY ENHANCED
 * @authority: @CEO - Abdullah Ahmed Al-Zahrani
 * @version: 2026.04.27
 * @description: تحديث المقاسات لتناسب العرض 100%؛ إضافة سمات id/name للنماذج؛ الحفاظ على الرفع وتصدير العقود بختم @CEO.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import { 
  CreditCard, TrendingUp, Landmark, ShieldCheck, 
  ArrowUpRight, PieChart, School, Zap, FileText, 
  CheckCircle2, Clock, UploadCloud, Printer, 
  ShieldAlert, Lock, AlertCircle, BarChart3, Globe, 
  Fingerprint, FileDown
} from 'lucide-react';
import { message, Tag, Progress, Tooltip } from 'antd';

const Financing = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const contractRef = useRef();

  // --- [ إدارة الجلسة والبيانات ] ---
  const userRole = localStorage.getItem('huger_user_role');
  const isAuthenticated = localStorage.getItem('huger_authenticated') === 'true';
  const API_URL = "https://portal.huger.sa/api.php";

  const [months, setMonths] = useState(24);
  const [isSigned, setIsSigned] = useState(false);
  const [signature, setSignature] = useState(localStorage.getItem('HUGER_CEOSIGN_V2') || "");
  const [docStatus, setDocStatus] = useState({ cr: 'idle', tax: 'idle' });

  // --- [ بروتوكول الحماية الداخلي ] ---
  useEffect(() => {
    const allowedRoles = ['CEO', 'Manager', 'Investor'];
    if (!isAuthenticated || !allowedRoles.includes(userRole)) {
      message.error(i18n.language === 'ar' ? "⚠️ خرق أمني: الوصول محصور للقيادة العليا." : "⚠️ Security Breach: Access Restricted.");
      navigate('/control-center');
    }
  }, [isAuthenticated, userRole, navigate, i18n.language]);

  const [labPackages] = useState([
    { 
      id: 1, 
      name: i18n.language === 'ar' ? 'مختبر STEAM المتكامل' : 'Integrated STEAM Lab', 
      price: 250000, 
      desc: i18n.language === 'ar' ? 'تجهيز شامل بأحدث أدوات العلوم والتقنية.' : 'Full equipment from HUGER factory.' 
    },
    { 
      id: 2, 
      name: i18n.language === 'ar' ? 'مركز الروبوت والذكاء الاصطناعي' : 'AI & Robotics Center', 
      price: 180000, 
      desc: i18n.language === 'ar' ? 'منظومة تعلم الآلة الموطنة محلياً.' : 'Locally integrated robotics system.' 
    },
    { 
      id: 3, 
      name: i18n.language === 'ar' ? 'ورشة التصنيع الرقمي (FabLab)' : 'Digital Fabrication (FabLab)', 
      price: 450000, 
      desc: i18n.language === 'ar' ? 'طابعات Zortrax ثلاثية وماكينات ليزر.' : 'Industrial 3D printing hub.' 
    }
  ]);
  const [selectedLab, setSelectedLab] = useState(labPackages[0]);

  const calculateInstallment = (price, months) => {
    if (userRole !== 'CEO' && userRole !== 'Manager') return "SECURED";
    const interest = 0.08; 
    const total = price + (price * interest);
    return Math.round(total / months).toLocaleString();
  };

  // --- [ محرك تصدير PDF ] ---
  const exportContractPDF = () => {
    const element = contractRef.current;
    const opt = {
      margin: 10,
      filename: `HUGER_Contract_${new Date().getTime()}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, backgroundColor: '#ffffff' },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  // --- [ بروتوكول رفع الملفات الحقيقي ] ---
  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('action', 'upload_doc');
    formData.append('doc_type', type);

    const hide = message.loading(i18n.language === 'ar' ? "جاري تشفير ورفع الوثيقة للمجلد السيادي..." : "Uploading to Sovereign Vault...", 0);

    try {
      const res = await axios.post(API_URL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.success) {
        setDocStatus(prev => ({ ...prev, [type]: 'verified' }));
        message.success(i18n.language === 'ar' ? "تم التحقق والإيداع بنجاح ✅" : "Verified & Deposited ✅");
      } else {
        message.error(res.data.message || "فشل الرفع");
      }
    } catch (err) {
      message.error("عائق في الاتصال بالخادم.");
    } finally {
      hide();
    }
  };

  // --- [ وظيفة التعميد المزدوج ] ---
  const handleSign = async () => {
    if (userRole !== 'CEO') return message.error(t('restricted_access'));
    if (signature.trim().length < 10) {
      message.warning(i18n.language === 'ar' ? "يرجى إدخال الاسم الكامل للاعتماد." : "Please enter full name.");
      return;
    }

    const hide = message.loading(i18n.language === 'ar' ? "جاري التعميد وتوليد الفاتورة..." : "Sealing & Invoicing...", 0);
    
    try {
      await axios.post(API_URL, {
        action: 'submit_financing',
        school_id: 1, 
        platform_name: 'Lendo', 
        total_amount: selectedLab.price
      });

      const resInv = await axios.post(API_URL, {
        action: 'create_invoice',
        school_id: 1,
        amount: selectedLab.price
      });

      if (resInv.data.success) {
        setIsSigned(true);
        localStorage.setItem('HUGER_CEOSIGN_V2', signature);
        message.success(i18n.language === 'ar' ? "تم التعميد! الفاتورة في الرادار ✅" : "Sealed! Invoice generated ✅");
      }
    } catch (error) {
      message.error("System sync failed.");
    } finally {
      hide();
    }
  };

  return (
    <div className={`min-h-screen bg-[#050B14] p-4 md:p-6 font-sans text-white ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      
      {/* Header - Compact Version */}
      <header className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-[#0A192F]/60 backdrop-blur-2xl p-6 rounded-[2.5rem] border border-white/5 shadow-xl relative overflow-hidden">
        <div className="flex items-center gap-4 w-full md:w-auto relative z-10 scale-90 md:scale-100 origin-right">
          <div className="bg-gradient-to-br from-[#F57C00] to-orange-700 p-3 rounded-xl shadow-lg">
            <Landmark size={24} />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black italic tracking-tighter uppercase leading-none">HUGER <span className="text-[#F57C00]">FINTECH</span></h1>
            <p className="text-blue-400 text-[7px] font-black uppercase tracking-[0.3em] mt-1 opacity-60 italic">Interactive Governance</p>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-black/40 px-4 py-2 rounded-xl border border-white/5 relative z-10">
          <Fingerprint size={16} className="text-[#F57C00]" />
          <div>
            <p className="text-[7px] text-slate-500 font-black uppercase tracking-widest">{t('access_authority')}</p>
            <p className="text-[10px] font-black text-white italic underline decoration-[#F57C00]">@{userRole} Verified</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-6">
          {/* حزم التجهيز - Compact Grid */}
          <section className="bg-[#0A192F]/40 p-6 rounded-[2.5rem] border border-white/5 shadow-lg">
            <h2 className={`text-sm font-black mb-6 flex items-center gap-2 italic border-[#F57C00] ${i18n.language === 'ar' ? 'border-r-2 pr-3' : 'border-l-2 pl-3'}`}>
              <Zap size={16} className="text-[#F57C00]" /> {i18n.language === 'ar' ? 'الحزم التجهيزية' : 'Packages'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {labPackages.map((pkg) => (
                <div 
                  key={pkg.id} 
                  id={`pkg_card_${pkg.id}`}
                  onClick={() => setSelectedLab(pkg)} 
                  className={`p-4 rounded-[1.5rem] border-2 transition-all duration-500 cursor-pointer ${selectedLab.id === pkg.id ? 'border-[#F57C00] bg-[#F57C00]/5' : 'border-white/5 bg-white/5 opacity-50'}`}
                >
                  <h4 className="font-black text-white italic mb-1 text-[11px] truncate">{pkg.name}</h4>
                  <p className="text-lg font-black text-white tracking-tighter">{pkg.price.toLocaleString()} SAR</p>
                </div>
              ))}
            </div>
          </section>

          {/* التحليل الائتماني - Compact Uploads */}
          <section className="bg-[#0A192F]/40 p-6 rounded-[2.5rem] border border-white/5 shadow-lg">
            <h2 className={`text-sm font-black mb-6 flex items-center gap-2 italic border-blue-500 ${i18n.language === 'ar' ? 'border-r-2 pr-3' : 'border-l-2 pl-3'}`}>
              <UploadCloud size={16} className="text-blue-500" /> {i18n.language === 'ar' ? 'التحليل الائتماني السيادي' : 'Sovereign Credit Analysis'}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {[ 
                {id:'cr', label: i18n.language === 'ar' ? 'السجل التجاري' : 'Commercial ID', name: 'commercial_register'}, 
                {id:'tax', label: i18n.language === 'ar' ? 'القوائم المالية' : 'Financial Statements', name: 'financial_statements'} 
              ].map((doc) => (
                <div key={doc.id} className="relative">
                  <input 
                    type="file" 
                    id={`upload_${doc.id}`} 
                    name={doc.name}
                    className="hidden" 
                    onChange={(e) => handleFileUpload(e, doc.id)} 
                    accept=".pdf,.jpg,.jpeg,.png" 
                  />
                  <label htmlFor={`upload_${doc.id}`} className={`p-6 rounded-[1.8rem] border-2 border-dashed transition-all cursor-pointer flex flex-col items-center gap-3 ${docStatus[doc.id] === 'verified' ? 'border-emerald-500 bg-emerald-500/5' : 'border-white/10 hover:border-[#F57C00]/40'}`}>
                    {docStatus[doc.id] === 'verified' ? <CheckCircle2 size={24} className="text-emerald-500" /> : <UploadCloud size={24} className="text-slate-700" />}
                    <span className="font-black text-[9px] tracking-widest uppercase text-center">{doc.label}</span>
                  </label>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* الحاسبة والعقد - Sticky Compact */}
        <div className="lg:col-span-5">
          <div className="bg-gradient-to-b from-[#0A192F] to-[#050B14] p-8 rounded-[2.5rem] border-2 border-white/5 text-center shadow-2xl lg:sticky lg:top-6">
            <div className="mb-6">
              <h2 className="text-4xl font-black text-white tracking-tighter italic">{calculateInstallment(selectedLab.price, months)}</h2>
              <p className="text-[10px] font-black text-blue-500 uppercase italic mt-2">{i18n.language === 'ar' ? 'ريال / شهرياً' : 'SAR / Monthly'}</p>
            </div>

            <div className="grid grid-cols-4 gap-2 mb-6">
              {[12, 24, 36, 48].map((m) => (
                <button 
                  key={m} 
                  id={`btn_months_${m}`}
                  name="installment_period"
                  onClick={() => setMonths(m)} 
                  className={`py-3 rounded-xl font-black text-[9px] border transition-all ${months === m ? 'bg-[#F57C00] border-[#F57C00] text-white shadow-lg shadow-orange-500/20' : 'bg-white/5 border-white/5 text-slate-500'}`}
                >
                  {m}m
                </button>
              ))}
            </div>

            <div className="border-t border-white/10 pt-6">
              {userRole === 'CEO' ? (
                !isSigned ? (
                  <div className="space-y-4">
                    <input 
                      id="ceo_signature_seal_input"
                      name="ceo_signature_seal"
                      type="text" 
                      value={signature} 
                      onChange={(e) => setSignature(e.target.value)} 
                      placeholder={i18n.language === 'ar' ? 'الاسم الرسمي للاعتماد...' : 'Official Seal Name...'} 
                      className="w-full bg-black/40 border border-white/10 p-4 rounded-[1.2rem] text-center text-xs text-white outline-none focus:border-[#F57C00]" 
                      autoComplete="name"
                    />
                    <button 
                      id="btn_grant_sovereign_financing_seal"
                      name="grant_seal_action"
                      onClick={handleSign} 
                      className="w-full bg-gradient-to-r from-[#F57C00] to-orange-600 py-4 rounded-[1.5rem] font-black text-white shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-2 text-xs border-none cursor-pointer"
                    >
                      {i18n.language === 'ar' ? 'منح التعميد السيادي' : 'Grant Seal'} <ShieldCheck size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div ref={contractRef} id="huger_sovereign_contract_pdf" className="p-4 border border-black rounded-2xl bg-white text-black text-right mb-4 w-full shadow-lg">
                        <div className="border-b border-black pb-2 mb-2">
                            <h2 className="text-[11px] font-black uppercase">عقد تجهيز تقني سيادي</h2>
                        </div>
                        <div className="space-y-1 text-[9px] font-bold">
                            <p>المشروع: {selectedLab.name}</p>
                            <p>القيمة: {selectedLab.price.toLocaleString()} SAR</p>
                            <p>الفترة: {months} شهر</p>
                        </div>
                        <div className="mt-4 border-t pt-2">
                            <p className="italic text-sm underline font-serif">{signature}</p>
                            <p className="text-[7px] uppercase font-black">Officially Sealed by @CEO</p>
                        </div>
                    </div>
                    <button 
                      id="btn_download_contract_pdf"
                      name="download_action"
                      onClick={exportContractPDF} 
                      className="w-full bg-emerald-600 py-3 rounded-xl font-black text-white flex items-center justify-center gap-2 mb-2 shadow-lg text-[10px] border-none cursor-pointer"
                    >
                      <FileDown size={16} /> {i18n.language === 'ar' ? 'تحميل PDF' : 'Download PDF'}
                    </button>
                  </div>
                )
              ) : (
                <div className="p-6 bg-black/60 rounded-[2rem] text-slate-700 italic text-[9px]">
                   <Lock size={20} className="mx-auto mb-2" /> Final Seal Required by @CEO
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="mt-12 text-center border-t border-white/5 pt-6 pb-6 opacity-20">
        <p className="text-[8px] font-black uppercase tracking-[0.4em]">HUGER Financial Ecosystem • v12.6.1 • 2026</p>
      </footer>
    </div>
  );
};

export default Financing;